import {
  faBuildingColumns,
  faCheck,
  faCreditCard,
  faGift,
  faGripDotsVertical,
  faMobileScreen,
  faMoneyBill,
  faMoneyBillAlt,
  faMoneyCheck,
  faPencil,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { DndSortableList } from 'src/components/DndSortableList';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { PaymentMethodForm } from 'src/components/PaymentMethodForm';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useSession } from 'src/contexts/SessionContext';
import { Cart } from 'src/models/cart/_types';
import { Client } from 'src/models/client/_types';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import { fetchAccessiblePaymentMethods } from 'src/models/payment-methods/payment-methods';
import { createPaymentMethod } from 'src/models/payment-methods/payment-methods';
import { FeatureFlag } from 'src/models/platform/_types';
import { updatePlatform } from 'src/models/platform/platform';
import { getTerminals } from 'src/models/terminal/terminal';
import { User } from 'src/models/user/_types';
import { getUserDisplayName } from 'src/models/user/utils';
import { isHeadTrainer } from 'src/models/user/utils';
import { ActionCard } from 'src/shared/ActionCard';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import { capitalizeFirstLetter } from 'src/utils/stringFormatter';
import toast from 'src/utils/toast';

import { UserSelectorChip } from './UserSelectorChip';

interface PaymentMethodSelectorProps {
  onSelect: (method: string | PaymentMethod) => void;
  onCheckNumberChange: (checkNumber: string) => void;
  cart: Cart;
  value: string | number | undefined;
  user?: User;
  client?: Client;
  goToSelectUser: () => void;
  /** When true, Stripe Terminal readers (swipe card) are omitted — e.g. past-due invoice pay has no matching backend flow. */
  hideCardPresentReaders?: boolean;
}

const DEFAULT_PAYMENT_METHOD_ORDER = [
  'reader',
  'tap_to_pay',
  'user_payment_methods',
  'cash',
  'check',
  'other',
  'free',
];

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  onSelect,
  onCheckNumberChange,
  cart,
  user,
  value,
  goToSelectUser,
  hideCardPresentReaders = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.paymentDialog',
  });
  const { supportsTapToPay } = useNativeBridge();

  const getPaymentMethodType = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'card':
        return t('creditCard');
      case 'us_bank_account':
        return t('bankAccount');
      default:
        return type;
    }
  };

  const getPaymentMethodIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'card':
        return faCreditCard;
      case 'us_bank_account':
        return faBuildingColumns;
      case 'cash':
        return faMoneyBill;
      case 'check':
        return faMoneyCheck;
      case 'free':
        return faGift;
      case 'other':
        return faMoneyBillAlt;
      case 'tap_to_pay':
        return faMobileScreen;
      default:
        return faCreditCard;
    }
  };

  const { session, user: currentUser } = useSession();
  const queryClient = useQueryClient();
  const [isReordering, setIsReordering] = useState(false);
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAddOtherPaymentLabel, setShowAddOtherPaymentLabel] =
    useState(false);
  const [newOtherPaymentLabel, setNewOtherPaymentLabel] = useState('');
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<
    string | null
  >(null);

  // Track if we've already set a default payment method to prevent infinite loops
  const hasSetDefaultPaymentMethodRef = useRef(false);

  const { data: allowTapToPay = false } = useQuery({
    queryKey: ['supportsTapToPay'],
    queryFn: supportsTapToPay,
  });

  const { data: terminals, isLoading: isLoadingTerminals } = useQuery({
    queryKey: ['terminals'],
    queryFn: () => getTerminals({}),
    select: (data) => data.filter((terminal) => terminal.status === 'online'),
  });

  const {
    data: accessiblePaymentMethods,
    isLoading: isLoadingAccessiblePaymentMethods,
  } = useQuery({
    queryKey: ['paymentMethods', 'accessible', user?.id],
    queryFn: () => {
      if (!user?.id) {
        throw new Error('User ID is not set');
      }
      return fetchAccessiblePaymentMethods(user.id);
    },
    enabled: !!user?.id,
  });

  const { mutate: createMethod } = useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => {
      toast.success(t('paymentMethodAddedSuccess'));
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', 'accessible', user?.id],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const allOneTimePurchases = cart?.cartItems?.every(
    (item) => item.paymentOption?.type === 'purchase'
  );

  const allowFreeTypes = ['Fbm::Package', 'WorkoutPlan', 'Group'];

  const allFreeTypes = cart?.cartItems?.every((item) =>
    allowFreeTypes.includes(item.itemType || '')
  );

  const containsSubscriptions = cart?.cartItems?.some((item) => {
    return item.paymentOption?.type === 'subscription';
  });

  const allowCashAndCheck =
    !hasFeatureFlag(session, FeatureFlag.HasCashAndCheck) &&
    allOneTimePurchases;

  const allowCardPresent =
    !hideCardPresentReaders && terminals && terminals.length > 0;

  const allowOtherPaymentMethods =
    allOneTimePurchases && (session?.otherPaymentMethodLabels || []).length > 0;

  const allowFree = allOneTimePurchases || allFreeTypes;

  const paymentMethodOrder = useMemo(
    () => session?.paymentMethodOrder || DEFAULT_PAYMENT_METHOD_ORDER,
    [session?.paymentMethodOrder]
  );

  useEffect(() => {
    setLocalOrder(paymentMethodOrder);
  }, [paymentMethodOrder]);

  useEffect(() => {
    // Only auto-select default payment method once per mount.
    // We intentionally do NOT reset this flag if `value` later changes:
    // re-firing this effect would call onSelect(defaultMethod) again and
    // overwrite cart fields the user has explicitly set (e.g. checkNumber).
    if (
      accessiblePaymentMethods &&
      accessiblePaymentMethods.length > 0 &&
      !value &&
      !hasSetDefaultPaymentMethodRef.current
    ) {
      const defaultMethod = accessiblePaymentMethods.find((pm) => pm.default);
      if (defaultMethod) {
        onSelect(defaultMethod);
        hasSetDefaultPaymentMethodRef.current = true;
      }
    }
  }, [accessiblePaymentMethods, onSelect, value]);

  const getPaymentMethodFromId = (id: string) => {
    return accessiblePaymentMethods?.find((pm) => pm.id.toString() === id);
  };

  const handleSelect = (method: any) => {
    const methodId = method.id.toString();

    if (method.type === 'card_present') {
      const reader = terminals?.find((r) => r.stripeId === method.id);
      if (reader) {
        onSelect(method);
      }
      return;
    }

    onSelect(getPaymentMethodFromId(methodId) || method);
  };

  const handleCheckNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onCheckNumberChange(event.target.value);
  };

  const handleAddOtherPaymentLabel = async () => {
    try {
      const existingLabels = session?.otherPaymentMethodLabels || [];
      await updatePlatform(session.id, {
        otherPaymentMethodLabels: [...existingLabels, newOtherPaymentLabel],
      });
      setShowAddOtherPaymentLabel(false);
      setNewOtherPaymentLabel('');
      queryClient.invalidateQueries({
        queryKey: ['session'],
      });
      toast.success(t('paymentMethodAddedSuccess'));
    } catch (error) {
      toast.error(t('failedToAddPaymentMethod'));
    }
  };

  const handleDeleteOtherPaymentLabel = async () => {
    if (!paymentMethodToDelete) return;

    try {
      const existingLabels = session?.otherPaymentMethodLabels || [];
      await updatePlatform(session.id, {
        otherPaymentMethodLabels: existingLabels.filter(
          (l) => l !== paymentMethodToDelete
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ['session'],
      });
      toast.success(t('paymentMethodRemovedSuccess'));
      setPaymentMethodToDelete(null);
    } catch (error) {
      toast.error(t('failedToRemovePaymentMethod'));
    }
  };

  const paymentMethods = [
    ...(accessiblePaymentMethods || []).map((pm) => {
      const isOtherUser = pm.user && pm.user.id !== user?.id;
      return {
        id: pm.id,
        primary: `${capitalizeFirstLetter(pm.brand) || ''} ${pm.last4}`,
        secondary: `${getPaymentMethodType(pm.paymentMethodType)}${
          pm.default ? ' (Default)' : ''
        }${isOtherUser ? ` - ${getUserDisplayName(pm.user)}` : ''}`,
        icon: getPaymentMethodIcon(pm.paymentMethodType),
        type: pm.paymentMethodType,
        default: pm.default,
      };
    }),
    ...(allowFree
      ? [
          {
            id: 'free',
            primary: t('free'),
            secondary: '',
            icon: getPaymentMethodIcon('free'),
            type: 'free',
          },
        ]
      : []),
    ...(allowCardPresent
      ? terminals?.map((reader) => ({
          id: reader.stripeId,
          primary: reader.label,
          secondary: t('swipeCard'),
          icon: getPaymentMethodIcon('card_present'),
          type: 'card_present',
        })) || []
      : []),
    ...(allowTapToPay
      ? [
          {
            id: 'tap_to_pay',
            primary: t('tapToPay'),
            secondary: t('tapToPayDescription'),
            icon: getPaymentMethodIcon('tap_to_pay'),
            type: 'tap_to_pay',
          },
        ]
      : []),
    ...(allowCashAndCheck
      ? [
          {
            id: 'cash',
            primary: t('cash'),
            secondary: '',
            icon: getPaymentMethodIcon('cash'),
            type: 'cash',
          },
          {
            id: 'check',
            primary: t('check'),
            secondary: '',
            icon: getPaymentMethodIcon('check'),
            type: 'check',
          },
        ]
      : []),
    ...(allowOtherPaymentMethods
      ? (session?.otherPaymentMethodLabels || []).map((label) => ({
          id: label,
          primary: capitalizeFirstLetter(label),
          secondary: t('otherPaymentMethod'),
          icon: getPaymentMethodIcon('other'),
          type: 'other',
        }))
      : []),
  ];

  const sortedPaymentMethods = paymentMethods.sort((a, b) => {
    const getOrderIndex = (method: any) => {
      if (method.type === 'card_present')
        return paymentMethodOrder.indexOf('reader');
      if (method.type === 'card' || method.type === 'us_bank_account')
        return paymentMethodOrder.indexOf('user_payment_methods');

      return paymentMethodOrder.indexOf(method.type);
    };

    return getOrderIndex(a) - getOrderIndex(b);
  });

  const getPaymentMethodLabel = (methodType: string) => {
    switch (methodType) {
      case 'reader':
        return t('cardReader');
      case 'tap_to_pay':
        return t('tapToPay');
      case 'user_payment_methods':
        return t('accountCardBank');
      case 'cash':
        return t('payWithCash');
      case 'check':
        return t('payWithCheck');
      case 'other':
        return t('payWithOther');
      case 'free':
        return t('addForFree');
      default:
        return methodType;
    }
  };

  const saveOrder = async () => {
    try {
      await updatePlatform(session.id, {
        paymentMethodOrder: localOrder,
      });
      setIsReordering(false);
      toast.success(t('paymentMethodOrderUpdated'));
      queryClient.invalidateQueries({
        queryKey: ['session'],
      });
    } catch (error) {
      toast.error(t('failedToUpdatePaymentMethodOrder'));
    }
  };

  if (isLoadingAccessiblePaymentMethods || isLoadingTerminals) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: '50svh' }}
      >
        <CircularProgress size="medium" />
      </Box>
    );
  }

  if (isReordering) {
    const items = localOrder.map((methodType) => ({
      id: methodType,
      label: getPaymentMethodLabel(methodType),
    }));

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('dragAndDropPaymentMethods')}
        </Typography>
        <DndSortableList<(typeof items)[0]>
          items={items}
          setItems={(newItems) => {
            if (Array.isArray(newItems)) {
              setLocalOrder(newItems.map((item) => item.id));
            } else {
              setLocalOrder(newItems(items).map((item) => item.id));
            }
          }}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          {(item) => (
            <ListItem
              primary={item.label}
              icon={faGripDotsVertical}
              sx={{
                borderRadius: 1,
              }}
            />
          )}
        </DndSortableList>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button onClick={() => setIsReordering(false)}>{t('cancel')}</Button>
          <Button variant="prominent" onClick={saveOrder}>
            {t('saveOrder')}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="payment-method-selector">
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', my: 1 }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <UserSelectorChip
            user={user}
            onChangeUser={goToSelectUser}
            size="medium"
          />
        </Stack>
        <Chip
          variant="outlined"
          color="primary"
          label={`${cart.cartItems.length} item${
            cart.cartItems.length === 1 ? '' : 's'
          }`}
        />
      </Stack>
      <Stack spacing={1} sx={{ mt: 3 }}>
        {sortedPaymentMethods.map((method) => (
          <ListItem
            key={method.id}
            onClick={() => handleSelect(method)}
            primary={method.primary}
            secondary={method.secondary}
            clickable
            rounded
            leftAdornment={<Icon icon={method.icon} sx={{ marginRight: 2 }} />}
            selected={value === method.id}
            rightAdornment={
              <>
                {value === method.id && (
                  <>
                    {method.id === 'check' && (
                      <TextField
                        placeholder={t('checkNumber')}
                        variant="outlined"
                        size="small"
                        value={cart.checkNumber || ''}
                        onChange={handleCheckNumberChange}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: '120px',
                          mr: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                              borderWidth: 1,
                            },
                          },
                        }}
                      />
                    )}
                    <Icon icon={faCheck} />
                  </>
                )}
                {isHeadTrainer(currentUser) && method.type === 'other' && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setPaymentMethodToDelete(method.id || null);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <Icon icon={faTrash} color="error" />
                  </IconButton>
                )}
              </>
            }
          />
        ))}
      </Stack>
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ActionCard
            title={t('addPaymentMethod')}
            description={t('addNewCardOrBank')}
            buttonText={t('addMethod')}
            icon={faPlus}
            onClick={() => setIsDialogOpen(true)}
          />
        </Grid>

        {isHeadTrainer(currentUser) && (
          <>
            {!containsSubscriptions && (
              <Grid size={{ xs: 12, lg: 4 }}>
                <ActionCard
                  title={t('addOtherPaymentType')}
                  description={t('addCustomPaymentMethod')}
                  buttonText={t('addType')}
                  icon={faPlus}
                  onClick={() => setShowAddOtherPaymentLabel(true)}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, lg: 4 }}>
              <ActionCard
                title={t('reorderPaymentMethods')}
                description={t('changePaymentMethodsOrder')}
                buttonText={t('reorder')}
                icon={faPencil}
                onClick={() => setIsReordering(true)}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={t('addPaymentMethod')}
        actions={[
          { label: t('cancel'), onClick: () => setIsDialogOpen(false) },
        ]}
        fullWidth
      >
        {user && (
          <PaymentMethodForm
            user={user}
            handlePaymentMethodAdded={(response) => {
              createMethod({
                token: response.setupIntent.id,
                userId: user.id,
                paymentMethodType: response.setupIntent.payment_method?.type,
                status:
                  response.setupIntent.status === 'requires_action'
                    ? 'unverified'
                    : 'verified',
                default: false,
              });
              setIsDialogOpen(false);
              queryClient.invalidateQueries({
                queryKey: ['paymentMethods', 'accessible'],
              });
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={showAddOtherPaymentLabel}
        onClose={() => setShowAddOtherPaymentLabel(false)}
        title={t('addOtherPaymentMethod')}
        actions={[
          {
            label: t('cancel'),
            onClick: () => setShowAddOtherPaymentLabel(false),
          },
          {
            label: t('add'),
            onClick: handleAddOtherPaymentLabel,
            variant: 'prominent',
            disabled: !newOtherPaymentLabel.trim(),
          },
        ]}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('customPaymentMethodHelperText')}
          </Typography>
          <TextField
            label={t('paymentMethodLabel')}
            value={newOtherPaymentLabel}
            onChange={(e) => setNewOtherPaymentLabel(e.target.value)}
            autoFocus
            fullWidth
          />
        </Stack>
      </Dialog>
      <ConfirmDialog
        open={!!paymentMethodToDelete}
        title={t('deletePaymentMethod')}
        actions={[
          {
            label: t('delete'),
            color: 'error',
            variant: 'prominent',
            onClick: handleDeleteOtherPaymentLabel,
          },
          {
            label: t('cancel'),
            onClick: () => setPaymentMethodToDelete(null),
            color: 'neutral',
            variant: 'bordered',
          },
        ]}
      >
        {t('deletePaymentMethodConfirmation')}
      </ConfirmDialog>
    </Box>
  );
};

export default PaymentMethodSelector;
